import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { getTQLNodeReference } from '../lib/tql-node';
import type { TQLNodeOption } from './tql-node-select';

interface Props {
  title: string;
  options: TQLNodeOption[];
  value?: null | number | string;
  'data-testid'?: string;
}

/**
 * Header of a dialog whose form is bound to a TQL node: the title and a hint
 * about the node identifier.
 *
 * As soon as a node is chosen the hint shows its reference — `<ci_type>_<id>`
 * is exactly what has to be typed into `ci_hint` rules and metric targets, and
 * without the identifier it cannot be assembled from anything the form shows.
 */
export function TQLNodeDialogTitle({
  title,
  options,
  value,
  'data-testid': testId = 'dialog-node-hint',
}: Props) {
  const { t } = useTranslation();

  const selected = options.find(
    (option) => option.value === String(value ?? ''),
  )?.node;
  const reference =
    selected?.status === 'resolved' && selected.ciTypeName
      ? getTQLNodeReference(selected)
      : null;

  return (
    <Stack component='span' gap={2}>
      <Text component='span' fw={600}>
        {title}
      </Text>
      <Text component='span' fz='xs' c='dimmed' data-testid={testId}>
        {reference
          ? t('hiTab.nodeReferenceHint', { ns: 'settings', reference })
          : t('hiTab.nodeIdHint', { ns: 'settings' })}
      </Text>
    </Stack>
  );
}